#version 300 es
precision highp float;
precision highp sampler3D;

// Ray-marches the ROI's signed-distance field (SDF) for the 3D preview. The SDF
// is stored exactly as elsewhere in the app: an R8 3D texture with the iso
// surface at 127.5/255 = 0.5 and "inside" = high byte values. The recipe (fixed
// step + bisection refine, normal from the SDF gradient) was validated in the
// Phase-0 ray-march spike against the real SDF encoding.

// Hard upper bound on the fixed-step march loop (GLSL requires a constant loop
// bound). Duplicates SHADER_MARCH_STEP_LIMIT in RoiPreviewRenderer, which
// derives the CPU step budget from it — keep the two in sync when tuning.
#define MARCH_STEP_LIMIT 512

in vec2 vNdc;
out vec4 fragColor;

uniform sampler3D uSdf;
uniform vec3 uEye;
uniform vec3 uRight;
uniform vec3 uUp;
uniform vec3 uFwd;
uniform float uTanHalf;
uniform float uAspect;
uniform float uStep;      // fixed march step, mm
uniform vec3 uBoxMin;     // ROI box minimum corner, scene mm
uniform vec3 uBoxMax;     // ROI box maximum corner, scene mm
uniform vec3 uSpacing;    // mm per voxel (per axis)
uniform vec3 uColor;
uniform float uAlpha;   // 1.0 seen directly; lower where a nearer surface hides it
uniform float uDepthNear; // distance from the eye mapped to depth 0, mm
uniform float uDepthFar;  // distance from the eye mapped to depth 1, mm

const float ISO = 0.5;    // 127.5 / 255

// Sample the R8 SDF at a scene-mm position -> [0,1]. Each ROI's sub-volume is
// its own box placed in the shared scene frame, so the mapping is per box
// rather than the single origin-centred box this preview started with.
float sampleSdf(vec3 posMm) {
    vec3 tc = (posMm - uBoxMin) / (uBoxMax - uBoxMin);
    return texture(uSdf, tc).r;
}

// Ray vs axis-aligned box [uBoxMin, uBoxMax]; returns (tNear, tFar) in mm.
vec2 intersectBox(vec3 ro, vec3 rd) {
    vec3 invD = 1.0 / rd;
    vec3 t0 = (uBoxMin - ro) * invD;
    vec3 t1 = (uBoxMax - ro) * invD;
    vec3 tMin = min(t0, t1);
    vec3 tMax = max(t0, t1);
    return vec2(
        max(max(tMin.x, tMin.y), tMin.z),
        min(min(tMax.x, tMax.y), tMax.z)
    );
}

void main() {
    vec3 rd = normalize(
        uFwd + vNdc.x * uAspect * uTanHalf * uRight + vNdc.y * uTanHalf * uUp
    );
    vec3 ro = uEye;

    vec2 tb = intersectBox(ro, rd);
    float t = max(tb.x, 0.0);
    // Every miss discards rather than painting the background: one ROI is drawn
    // per pass into a buffer already cleared to the background, so writing it
    // here would erase a nearer ROI drawn by an earlier pass.
    if (tb.y <= t) {
        discard;
    }

    // Fixed-step march (not sphere tracing: the SDF is clamped to a +/-10mm
    // band, so true-distance steps are unreliable, but a fixed step only needs
    // the sign crossing, which is exact at the surface).
    float prev = sampleSdf(ro + rd * t) - ISO;
    float tHit = -1.0;
    for (int s = 0; s < MARCH_STEP_LIMIT; s++) {   // CPU budget derives from this
        t += uStep;
        if (t > tb.y) {
            break;
        }
        float cur = sampleSdf(ro + rd * t) - ISO;
        if (cur >= 0.0 && prev < 0.0) {
            float lo = t - uStep;
            float hi = t;
            for (int r = 0; r < 5; r++) {   // bisection refine the crossing
                float mid = 0.5 * (lo + hi);
                if (sampleSdf(ro + rd * mid) - ISO >= 0.0) {
                    hi = mid;
                } else {
                    lo = mid;
                }
            }
            tHit = hi;
            break;
        }
        prev = cur;
    }
    if (tHit < 0.0) {
        discard;
    }

    vec3 p = ro + rd * tHit;
    // Gradient with an equal mm epsilon on each axis, so the normal is computed
    // in patient-mm space and anisotropic voxel spacing is handled for free.
    float e = min(uSpacing.x, min(uSpacing.y, uSpacing.z));
    vec3 grad = vec3(
        sampleSdf(p + vec3(e, 0.0, 0.0)) - sampleSdf(p - vec3(e, 0.0, 0.0)),
        sampleSdf(p + vec3(0.0, e, 0.0)) - sampleSdf(p - vec3(0.0, e, 0.0)),
        sampleSdf(p + vec3(0.0, 0.0, e)) - sampleSdf(p - vec3(0.0, 0.0, e))
    );
    vec3 normal = normalize(-grad);   // inside is high => outward = -gradient

    vec3 lightDir = normalize(uEye - p);   // headlight
    float diffuse = max(dot(normal, lightDir), 0.0);
    float rim = pow(1.0 - max(dot(normal, lightDir), 0.0), 3.0) * 0.25;
    vec3 color = uColor * (0.22 + 0.78 * diffuse) + rim;

    // mild front-to-back depth cue for shape readability
    float depth = clamp((tHit - tb.x) / (tb.y - tb.x), 0.0, 1.0);
    color *= mix(1.05, 0.8, depth);

    fragColor = vec4(pow(color, vec3(0.9)), uAlpha);

    // Depth so several ROIs, each drawn in its own pass, occlude each other
    // correctly. tHit is a distance along a normalised ray, so this is true
    // distance from the eye and the nearest surface along each ray wins.
    gl_FragDepth = clamp(
        (tHit - uDepthNear) / (uDepthFar - uDepthNear), 0.0, 1.0
    );
}
